import { useAppDispatch, useAppSelector } from '../../useReduxToolkit';
import * as realtime from '../../../features/realtime/realtime.slice';
import {
  RealtimeMessageType,
  RealtimeRole,
} from '../../../features/realtime/realtime.types';
import {
  type ScreenshareEventData,
  ScreenshareRequestPayload,
} from '../screenshareState.types';
import { useCallParticipants } from '../../useCallParticipants';
import {
  getCurrentRequestedMember,
  getScreenshareStartedTime,
} from '../../../features/screenshare/screenshare.selectors';
import { getLogger } from '../../../libs/logger';
import type { ScreenshareStoppedReason } from '../../../features/screenshare/screenshare.types';
import { TrackedEvents } from '../../../features/tracking/track';
import { useTrack } from '../../useTrack';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import { TimerObserver } from '../../../libs/timerObserver';
import { session } from '../../../webrtc/session';
import { toast } from '@doxyme/ui/components';
import { useIntl } from '@doxyme/locale';
const logger = getLogger('useStopScreenshareRequest');
/**
 * Stop the active screen sharing.
 * Applies to self and any other participant.
 */

export const useStopScreenshareRequest = (): ((
  eventData?: ScreenshareEventData,
) => Promise<void>) => {
  const track = useTrack();
  const dispatch = useAppDispatch();
  const { screenSharingParticipant, callParticipantsMap } =
    useCallParticipants();
  const { role } = useAppSelector(getApolloSessionDetails);
  const startedAt = useAppSelector(getScreenshareStartedTime);
  const currentRequestedMemberId = useAppSelector(getCurrentRequestedMember);
  const { formatMessage } = useIntl();

  const isProviderStoppingRemoteSharersShare =
    role === RealtimeRole.Provider && screenSharingParticipant !== undefined;

  let stopReason: ScreenshareStoppedReason =
    isProviderStoppingRemoteSharersShare
      ? 'DO_NOT_TRACK'
      : role === RealtimeRole.Provider
        ? 'PROVIDER_STOPPED_OWN'
        : 'PATIENT_STOPPED_OWN';

  const stopScreenshareRequest = async (
    eventData?: ScreenshareEventData,
  ): Promise<void> => {
    // This is the case in which the provider sends the stop screenshare signal to the patient
    if (role === 'Provider' && screenSharingParticipant !== undefined) {
      await dispatch(
        realtime.sendMessage({
          id: screenSharingParticipant.id,
          message: {
            messageType: RealtimeMessageType.ScreenshareRequest,
            payload: {
              eventName: ScreenshareRequestPayload.Stop,
              eventData,
            },
          },
        }),
      );
    }

    if (role === 'Patient') {
      // This is the patient receiving the stop screenshare signal from the provider
      if (eventData?.realtimeMemberId !== undefined) {
        stopReason = 'PROVIDER_STOPPED_PATIENT';
        const provider = callParticipantsMap[eventData.realtimeMemberId];

        toast.warning({
          title: formatMessage(
            { id: 'screenShare.providerStoppedYourScreenshare' },
            { name: provider?.username },
          ),
        });
      } else {
        //  Let the Provider know the Patient has stopped sharing over realtime in case the session is not yet subscribed to.
        if (currentRequestedMemberId === undefined) {
          throw new Error(
            'Screenshare stopped but currentRequestedMemberId is undefined',
          );
        }
        await dispatch(
          realtime.sendMessage({
            id: currentRequestedMemberId,
            message: {
              messageType: RealtimeMessageType.ScreenshareRequest,
              payload: {
                eventName: ScreenshareRequestPayload.Stopped,
                eventData,
              },
            },
          }),
        );
      }
    }

    // If it's stopped remotely by provider we don't track it on the provider side, only on the patient's
    if (stopReason !== 'DO_NOT_TRACK') {
      // Tracking in Segment
      void track(TrackedEvents.ScreenshareEnded, {
        sharedBy: screenSharingParticipant?.identity?.role ?? role,
        stoppedBy:
          stopReason === 'PATIENT_STOPPED_OWN'
            ? RealtimeRole.Patient
            : RealtimeRole.Provider,
      });

      // Tracking in DataDog
      logger.info('[screenshare/completed]', {
        startedAt,
        endedAt: Date.now(),
        duration: TimerObserver.fromMilliseconds(
          Date.now() - (startedAt ?? Date.now()),
        ),
        reason: stopReason,
      });
    }
    await session.stopScreenShare();
  };

  return stopScreenshareRequest;
};
